// ** React Imports
import { Link, useLocation } from 'react-router-dom'
import { useEffect, useRef, useState, cloneElement } from 'react'

// ** Third Party Components
import classnames from 'classnames'
import { Badge } from 'dsm-react-lib'

// ** Vertical Menu Items Component
import VerticalNavMenuItems from './VerticalNavMenuItems'

// ** Utils
import { isNavGroupActive, getAllParents } from '@layouts/utils'
import { createPortal } from 'react-dom'

const VerticalNavMenuGroup = ({
  item,
  groupActive,
  setGroupActive,
  activeItem,
  setActiveItem,
  groupOpen,
  setGroupOpen,
  parentItem,
  menuCollapsed,
  menuHover,
  routerProps,
  currentActiveItem
}) => {
  // ** Current Val
  const currentURL = useLocation().pathname

  const [submenuPosition, setSubmenuPosition] = useState({ top: 0, left: 0 })

  // ** Ref for dropdown container
  const dropdownRef = useRef(null)
  const submenuRef = useRef(null)

  const isSubMenuOpen =
    (groupActive && groupActive.includes(item.id)) || (groupOpen && groupOpen.includes(item.id))
  const isSubMenuPositioned = submenuPosition.top !== 0 && submenuPosition.left !== 0

  // ** Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        (!submenuRef.current || !submenuRef.current.contains(event.target))
      ) {
        // Close the dropdown if it's currently open
        if (groupOpen.includes(item.id)) {
          setGroupOpen(groupOpen.filter((id) => id !== item.id))
        }
        if (groupActive.includes(item.id)) {
          setGroupActive(groupActive.filter((id) => id !== item.id))
        }
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [groupOpen, groupActive, item.id, setGroupOpen, setGroupActive])

  // ** Close dropdown function
  const closeDropdown = () => {
    // Force close the dropdown by removing from both arrays
    setGroupOpen((prev) => prev.filter((id) => id !== item.id))
    setGroupActive((prev) => prev.filter((id) => id !== item.id))

    // Also try with a small delay to ensure state updates
    setTimeout(() => {
      setGroupOpen((prev) => prev.filter((id) => id !== item.id))
      setGroupActive((prev) => prev.filter((id) => id !== item.id))
    }, 0)
  }

  // ** Toggles Open Group
  const toggleOpenGroup = (item, parentItem) => {
    let openArr = groupOpen
    let allParents

    if (parentItem) {
      allParents = getAllParents(parentItem, 'id')
      allParents.pop()
    }

    // ** If user clicked on menu group inside already opened group i.g. when user click on blog group inside pages group
    if (groupOpen && allParents && groupOpen[0] === allParents[0]) {
      groupOpen.includes(item) ? openArr.splice(openArr.indexOf(item), 1) : openArr.push(item)
    } else {
      openArr = []
      if (!groupOpen.includes(item)) {
        openArr.push(item)
      }
    }

    // ** Set Open Group
    setGroupOpen([...openArr])
  }

  // ** Toggle Active Group
  const toggleActiveGroup = (item, parentItem) => {
    let activeArr = groupActive
    let allParents

    if (parentItem) {
      allParents = getAllParents(parentItem, 'id')
      activeArr = allParents
    } else {
      activeArr.includes(item) ? activeArr.splice(activeArr.indexOf(item), 1) : activeArr.push(item)
    }

    // ** Set open group removing any activegroup item present in opengroup state
    const openArr = groupOpen.filter((val) => !activeArr.includes(val))
    setGroupOpen([...openArr])

    // **  Set Active Group
    setGroupActive([...activeArr])
  }

  // ** On Group Item Click
  const onCollapseClick = (e, item) => {
    if (
      (groupActive && groupActive.includes(item.id)) ||
      isNavGroupActive(item.children, currentURL, routerProps)
    ) {
      toggleActiveGroup(item.id)
    } else {
      toggleOpenGroup(item.id, parentItem)
    }

    e.preventDefault()
  }

  // ** Returns condition to add open class
  const openClassCondition = (id) => {
    if ((menuCollapsed && menuHover) || menuCollapsed === false) {
      if (groupActive.includes(id) || groupOpen.includes(item.id)) {
        return true
      }
    } else if (groupActive.includes(id) && menuCollapsed && menuHover === false) {
      return false
    } else {
      return null
    }
  }

  useEffect(() => {
    openSubmenu()
  }, [isSubMenuOpen])

  const openSubmenu = () => {
    if (isSubMenuOpen && dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect()

      const viewportWidth = window.innerWidth
      const viewportHeight = window.innerHeight
      const submenuWidth = rect.width || 200 // Approximate width
      const submenuHeight = rect.height || 80 // Approximate max height
      const gap = 0

      // Calculate available space in all directions
      const spaceRight = viewportWidth - rect.right
      const spaceLeft = rect.left
      const spaceTop = rect.top
      const spaceBottom = viewportHeight - rect.bottom

      let left = rect.right + gap
      let top = rect.top

      // Determine horizontal position (left or right)
      if (spaceRight >= submenuWidth + gap) {
        // Position to the right (default)
        left = rect.right + gap
      } else if (spaceLeft >= submenuWidth + gap) {
        // Position to the left
        left = rect.left - submenuWidth - gap
      } else {
        // Not enough space on either side, position inside viewport
        if (spaceRight > spaceLeft) {
          left = viewportWidth - submenuWidth - gap
        } else {
          left = gap
        }
      }

      // Determine vertical position (top or bottom alignment)
      if (spaceBottom >= submenuHeight) {
        // Align with trigger top (default)
        top = rect.top
      } else if (spaceTop >= submenuHeight) {
        // Position above the trigger
        top = rect.bottom - submenuHeight
      } else {
        // Center vertically in viewport if no space above or below
        const centerY = viewportHeight / 2
        top = Math.max(
          gap,
          Math.min(centerY - submenuHeight / 2, viewportHeight - submenuHeight - gap)
        )
      }

      // Ensure submenu doesn't go outside viewport bounds
      left = Math.max(gap, Math.min(left, viewportWidth - submenuWidth - gap)) - 15
      top = Math.max(gap, Math.min(top, viewportHeight - submenuHeight - gap))
      setSubmenuPosition({
        top,
        left
      })
    }
  }

  const navSubMenu = () => {
    return createPortal(
      <div
        ref={submenuRef}
        className={classnames(
          'navigation submenu-navigation fixed bg-[var(--submenu-bg)] rounded  p-1 list-none overflow-y-auto z-[9999] shadow-md transition-all duration-300 ease-in-out',
          {
            block: isSubMenuOpen && isSubMenuPositioned,
            hidden: !isSubMenuOpen || !isSubMenuPositioned
          }
        )}
        style={{
          top: `${submenuPosition.top}px`,
          left: `${submenuPosition.left}px`
        }}>
        <VerticalNavMenuItems
          items={item.children}
          groupActive={groupActive}
          setGroupActive={setGroupActive}
          groupOpen={groupOpen}
          setGroupOpen={setGroupOpen}
          toggleActiveGroup={toggleActiveGroup}
          parentItem={item}
          menuCollapsed={false}
          menuHover={menuHover}
          routerProps={routerProps}
          currentActiveItem={currentActiveItem}
          activeItem={activeItem}
          setActiveItem={setActiveItem}
          closeDropdown={closeDropdown}
        />
      </div>,
      document.body
    )
  }

  // Check if any child is currently active
  const isGroupActive =
    groupActive.includes(item.id) || isNavGroupActive(item.children, currentURL, routerProps)

  return (
    <li
      ref={dropdownRef}
      className={classnames('nav-item has-sub !py-[3px]', {
        open: openClassCondition(item.id),
        'menu-collapsed-open': groupActive.includes(item.id),
        'sidebar-group-active': isGroupActive
      })}>
      <Link
        className={`d-flex align-items-center !p-[8px] !rounded-[4px] ${
          isGroupActive
            ? '!bg-[var(--nav-active-bg)] active:!bg-[var(--nav-active-bg)]'
            : 'hover:bg-[var(--nav-hover-bg)]'
        }`}
        to="/"
        onClick={(e) => onCollapseClick(e, item)}>
        {menuCollapsed ? (
          <span className="flex items-center justify-center w-full h-full">
            {cloneElement(item.icon, {
              color: isGroupActive ? 'var(--nav-active-text)' : 'var(--nav-icon)'
            })}
          </span>
        ) : (
          cloneElement(item.icon, {
            color: isGroupActive ? 'var(--nav-active-text)' : 'var(--nav-icon)'
          })
        )}

        {!menuCollapsed && (
          <span
            className={`menu-title text-truncate ml-[8px] ${
              isGroupActive ? 'text-[var(--nav-active-text)]' : 'text-[var(--nav-text)]'
            }`}>
            {item.title}
          </span>
        )}
        {item.badge && item.badgeText ? (
          <Badge className="ml-auto mr-1" color={item.badge} type="pill" label={item.badgeText} />
        ) : null}
      </Link>

      {/* Render Child Dropdown to the Right */}
      {isSubMenuOpen ? navSubMenu() : null}
    </li>
  )
}

export default VerticalNavMenuGroup
